/**
 * Identity re-key (AW migration — plan P4.5 mechanism).
 *
 * Under Cloud Identity a person's uid is their Directory id; member rows
 * written under Firebase still carry the Firebase uid, so the person is
 * org-less until re-keyed. This panel always runs a dry run first and shows
 * the plan; Apply needs a reason and only touches rows marked "Will re-key".
 *
 *   member given → POST /ops_admin/orgs/:org/members/:id/rekey
 *   no member    → POST /ops_admin/identity/rekey_backfill { org_id }  (owner)
 */
import { useEffect, useState } from 'react';
import { api } from '../../api';
import ReasonField from './ReasonField';

const STATUS = {
  current: { label: 'Up to date', cls: 'ok' },
  ready: { label: 'Will re-key', cls: 'warn' },
  unmatched: { label: 'No Cloud Identity account', cls: 'muted' },
  conflict: { label: 'Conflict', cls: 'error' },
  error: { label: 'Lookup failed', cls: 'error' },
};

function shortUid(uid) {
  if (!uid) return '—';
  return uid.length > 14 ? `${uid.slice(0, 6)}…${uid.slice(-4)}` : uid;
}

export default function RekeyPanel({ orgId, member, onClose, onApplied }) {
  const [plans, setPlans] = useState(null);
  const [summary, setSummary] = useState(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [applied, setApplied] = useState(false);
  // Repair mode: rows in this org still keyed by a login that is no longer a
  // member (a revoke before the re-key, or a re-invite under a new identity).
  const [orphans, setOrphans] = useState(null);
  const [oldUid, setOldUid] = useState('');
  const [typedUid, setTypedUid] = useState('');

  async function run(apply, uid = oldUid) {
    setBusy(true); setError(null);
    try {
      if (member) {
        const body = { dry_run: !apply, reason, ...(uid ? { old_uid: uid } : {}) };
        const res = await api.post(`/ops_admin/orgs/${orgId}/members/${member.id}/rekey`, body);
        setPlans([res.plan]); setSummary(null);
      } else {
        const res = await api.post('/ops_admin/identity/rekey_backfill', { org_id: orgId, apply, reason });
        setPlans(res.plans || []); setSummary(res.summary || null);
      }
      if (apply) { setApplied(true); onApplied?.(); }
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  useEffect(() => { run(false); }, [orgId, member?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!member) return;
    api.get(`/ops_admin/orgs/${orgId}/identity/orphans`)
      .then(res => setOrphans(res.orphans || []))
      .catch(() => setOrphans([]));
  }, [orgId, member?.id, applied]);

  function planRepair(uid) {
    const u = (uid || '').trim();
    if (!u) return;
    setOldUid(u);
    run(false, u);
  }
  const canRepair = member && plans && plans[0]?.status === 'current' && !applied;

  const actionable = (plans || []).filter(p => p.status === 'ready' || (p.status === 'current' && !p.identity_email_before)).length;
  const canApply = !busy && !applied && actionable > 0 && reason.trim().length > 0;

  return (
    <div className="rekey-panel" style={{ padding: 12, border: '1px solid var(--border, #e2e8f0)', borderRadius: 8, margin: '8px 0' }}>
      <div className="ops-page-header" style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>
          {member ? `Re-key identity — ${member.email}` : 'Re-key members by email (Firebase uid → Cloud Identity id)'}
        </h3>
        <button className="btn btn-sm btn-ghost" onClick={onClose}>Close</button>
      </div>
      <p className="muted" style={{ fontSize: 12, marginTop: 0 }}>
        Looks up each member's work email in the Cloud Identity directory and rewrites every row keyed by the old
        uid (membership, cases, grants, notebooks, attribution) to the Directory id. Dry run shown first; conflicts
        are never merged.
      </p>

      {error && <p className="error-text">{error}</p>}
      {busy && !plans && <p className="loading-text">Planning…</p>}

      {plans && (
        <table className="ops-table" style={{ fontSize: 12 }}>
          <thead>
            <tr><th>Email</th><th>Old uid</th><th>Directory id</th><th>Sign-in username</th><th>Status</th><th>Detail</th></tr>
          </thead>
          <tbody>
            {plans.length === 0 && <tr><td colSpan={6} className="empty-row">No members</td></tr>}
            {plans.map(p => {
              const s = STATUS[p.status] || { label: p.status, cls: 'muted' };
              const rows = p.applied && Object.keys(p.applied).length
                ? Object.entries(p.applied).map(([k, v]) => `${k}: ${v}`).join(', ')
                : null;
              return (
                <tr key={p.member_id}>
                  <td>{p.email || '—'}</td>
                  <td className="mono" title={p.old_uid}>{shortUid(p.old_uid)}</td>
                  <td className="mono" title={p.new_uid || ''}>{shortUid(p.new_uid)}</td>
                  <td className="mono">{p.identity_email || '—'}</td>
                  <td><span className={`badge badge-${s.cls}`}>{rows ? 'Applied' : s.label}</span></td>
                  <td>{rows ? <span className="mono">{rows}</span> : p.detail}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {summary && (
        <p className="muted" style={{ fontSize: 12 }}>
          {Object.entries(summary).map(([k, v]) => `${k}: ${v}`).join(' · ')}
        </p>
      )}

      {canRepair && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed var(--border, #e2e8f0)' }}>
          <strong style={{ fontSize: 12 }}>Repair: work still keyed by a login that is no longer a member</strong>
          {orphans === null && <p className="loading-text" style={{ fontSize: 12 }}>Looking…</p>}
          {orphans && orphans.length === 0 && (
            <p className="muted" style={{ fontSize: 12, margin: '4px 0' }}>No orphaned cases in this org.</p>
          )}
          {orphans && orphans.length > 0 && (
            <ul style={{ fontSize: 12, margin: '4px 0', paddingLeft: 18 }}>
              {orphans.map(o => (
                <li key={o.uid} style={{ marginBottom: 4 }}>
                  <span className="mono" title={o.uid}>{shortUid(o.uid)}</span> — {o.cases} case{o.cases === 1 ? '' : 's'}
                  {' '}
                  <button className="btn btn-sm btn-ghost" onClick={() => planRepair(o.uid)} disabled={busy}>
                    Move to {member.email}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="button-row" style={{ marginTop: 4 }}>
            <input className="form-input" style={{ fontSize: 12, maxWidth: 320 }} placeholder="…or a previous uid (Firebase console)"
              value={typedUid} onChange={e => setTypedUid(e.target.value)} />
            <button className="btn btn-sm btn-ghost" onClick={() => planRepair(typedUid)} disabled={busy || !typedUid.trim()}>Plan repair</button>
          </div>
          {oldUid && plans[0]?.status === 'current' && (
            <p className="muted" style={{ fontSize: 12 }}>Planning the move of rows keyed by <span className="mono">{shortUid(oldUid)}</span>…</p>
          )}
        </div>
      )}

      {!applied && plans && actionable > 0 && (
        <div style={{ marginTop: 8 }}>
          <ReasonField value={reason} onChange={setReason} placeholder="Reason (e.g. AW cutover re-key, ticket #)" />
          <div className="button-row">
            <button className="btn btn-sm btn-primary" onClick={() => run(true)} disabled={!canApply}>
              {busy ? 'Applying…' : `Apply re-key (${actionable})`}
            </button>
            <button className="btn btn-sm btn-ghost" onClick={() => run(false, oldUid)} disabled={busy}>Re-plan</button>
          </div>
        </div>
      )}
      {applied && <p className="ok-text" style={{ fontSize: 12 }}>Re-key applied. The person must sign in again to pick up their membership.</p>}
    </div>
  );
}
